import { useState, useEffect, useContext, useMemo } from 'react';
import { getCategories, getVideos } from '../../api/video-library';

import VideoCard from './Components/VideoCard';
import CreateVideoModal from './Components/CreateVideoModal';
import { Modalv2Context } from '../../Contexts/Modalv2Context';
import PageHeading from '../../Components/PageHeading';
import GeneralButton from '../../Components/GeneralButton';

//mui imports====
import { Box } from '@mui/system';
import useStyles from './styles';
import Typography from '@mui/material/Typography';
import { Grid, Paper } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Loader from '../../Components/Loader';

//===============

const VideoLibrary = () => {
	const classes = useStyles('./styles');
	const [categorySelected, setCategorySelected] = useState('');
	const [categories, setCategories] = useState({});
	const { updateModalContent } = useContext(Modalv2Context);
	const [data, setData] = useState([]);
	const [loaderState, setLoaderState] = useState('CATEGORIES_LOADING'); // there are 3 states CATEGORIES_LOADING , VIDEOS_LOADING , NO_LOADING

	useEffect(() => {
		(async function () {
			try {
				let { data } = await getCategories();
				if (data.data.categories) setCategories(data.data.categories);
			} catch (err) {
				alert(err);
			}
			setLoaderState('NO_LOADING');
		})();
	}, []);

	useEffect(() => {
		if (!categorySelected) setData((data) => []);
		if (categorySelected) {
			handleUpdateData(categorySelected);
			setLoaderState('VIDEOS_LOADING');
		}
	}, [categorySelected]);

	const handleUpdateData = async (category) => {
		try {
			let { data } = await getVideos(category);
			const videosData = data.data.videos;
			if (videosData) {
				// filtering videos that are not a part of publisher's mapped playlist.
				const filteredVideos = videosData.filter((videoObj) => !videoObj.playlistId);
				setData((x) => filteredVideos);
			}
		} catch (err) {
			alert(err);
		}
		setLoaderState('NO_LOADING');
	};

	const updateVideoDocOfAudioVersion = (index, audioVersion, videoDoc) => {
		setData((prevDetails) => {

			if (!prevDetails || !prevDetails[index]) return prevDetails;
			const updatedVideo = {
				...prevDetails[index],
				// Only update the specific audioVersion property
				[audioVersion]: {
					...prevDetails[index][audioVersion],
					...videoDoc
				}
			};
			const newDetails = [...prevDetails];
			newDetails[index] = updatedVideo;
			return newDetails;
		});
	};

	const pagePath = useMemo(() => {
		if (categorySelected) {
			return ['Dashboard', 'Video Library', `Category (${categorySelected})`];
		}
		return ['Dashboard', 'Video Library', 'Categories'];
	}, [categorySelected]);

	return (
		<Paper className={classes.dashboardMainContent}>
			<Box className={classes.pageHeadingAndButtonsContainer}>
				<PageHeading headingName="Video Library" headingPath={pagePath} />
				<Box className={classes.buttonsContainer}>
					<GeneralButton
						size="large"
						variant="contained"
						fontWeight={600}
						name="VIDEO_LIBRARY_OPEN_CREATE_VIDEO_MODAL"
						className={classes.circularButtonStyles}
						onClick={() => updateModalContent(<CreateVideoModal categories={categories} />)}
					>
						New Video +
					</GeneralButton>
				</Box>
			</Box>

			{categorySelected === '' ? (
				<Grid container spacing={0} className={classes.categoriesContainer}>
					{loaderState === 'CATEGORIES_LOADING' && <Loader />}
					{loaderState === 'NO_LOADING' && (
						<>
							{Object.keys(categories).map((categoryName) => {
								if (categoryName === 'dateCreated') {
									return null;
								}
								return (
									<Grid item xs={3} key={categoryName}>
										<CategoryCard
											key={categoryName}
											categoryName={categoryName}
											setcategorySelected={setCategorySelected}
										/>
									</Grid>
								);
							})}
						</>
					)}
				</Grid>
			) : (
				<>
					<Grid container spacing={1} className={classes.categoriesContainer}>
						<Grid item xs={12} style={{ display: 'flex' }}>
							<ArrowBackIcon
								color="primary"
								fontSize="large"
								style={{ cursor: 'pointer' }}
								onClick={() => setCategorySelected('')}
							/>
							<Typography variant="h5" style={{ marginLeft: '1rem' }} color="primary">
								{categorySelected}
							</Typography>
						</Grid>
						{loaderState === 'VIDEOS_LOADING' && <Loader />}
						{loaderState === 'NO_LOADING' && (
							<>
								{data.map((details, index) => {
									return (
										<Grid item xs={3} key={details.path}>
											<VideoCard
												videoDetails={details}
												index={index}
												updateVideoDocOfAudioVersion={updateVideoDocOfAudioVersion}
											/>
										</Grid>
									);
								})}
							</>
						)}
					</Grid>
				</>
			)}
		</Paper>
	);
};

const CategoryCard = ({ categoryName, setcategorySelected }) => {
	const classes = useStyles();
	const [elevation, setElevation] = useState(3);
	return (
		<Paper
			elevation={elevation}
			className={classes.categoryCard}
			sx={{ borderRadius: '15px !important' }}
			onClick={() => setcategorySelected(categoryName)}
			onMouseEnter={() => setElevation(5)}
			onMouseLeave={() => setElevation(3)}
		>
			<Typography color="primary.grey" fontWeight={600} variant="h5">
				{categoryName}
			</Typography>
		</Paper>
	);
};

export default VideoLibrary;
